import {naturalSort} from '@benchling/js-utils';
import classNames from 'classnames';
import gql from 'graphql-tag';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {ProcessClientOrigin, ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getDefaultSearchSearchParams} from 'nav/sidepanel/listing/SearchSearchParams';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import * as SidepanelReduxActions from 'nav/sidepanel/SidepanelReduxActions';
import {
  SUPPORTED_DASHBOARD_TYPES_BY_PROCESS_CLIENT_ORIGIN,
  isDashboardTypeAssignable,
} from 'processes/dashboard/ProcessManagementSidepanelConstants';
import {
  AssignedProcessTasksQuery,
  AssignedProcessTasksQueryVariables,
} from 'processes/home/__generated/AssignedProcessTasksQuery';
import {getEnabledClientOrigins} from 'processes/ProcessConstants';
import TaskStatusIcon from 'processes/status/TaskStatusIcon';
import {sortTaskStatuses} from 'processes/status/TaskStatusUtil';
import {getStore} from 'redux-stores/Store';
import {FilterKey, keyToEmptyFilter, Filter} from 'search/SearchConstants';
import * as SearchUtil from 'search/SearchUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {WHITE} from 'ui-library/Colors';
import * as nyc from 'ui-library/NYC';

interface AssignedProcessTaskStatusSummaryItemProps {
  count: number;
  displayName: string;
  statusType: ProcessTaskStatusType;
  onClick: () => void;
}

export function AssignedProcessTaskStatusSummaryItem(
  props: AssignedProcessTaskStatusSummaryItemProps
): JSX.Element {
  const {count, displayName, statusType, onClick} = props;
  return (
    <div className="homeSidepanel-today-item-with-icon">
      {/* override the icon to white because not all status icons show up clearly on a blue background */}
      <TaskStatusIcon type={statusType} overrideColor={WHITE} />
      <span
        className="whiteLink"
        onClick={onClick}
        {...getTestProps({
          component: 'AssignedProcessTaskStatusSummaryItem',
          element: 'root',
          key: statusType,
        })}
        data-amp-safe={false}
      >
        {`${count} ${displayName}`}
      </span>
    </div>
  );
}

export const ASSIGNED_PROCESS_TASKS_QUERY = gql`
  query AssignedProcessTasksQuery($statusTypes: [ProcessTaskStatusType]!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    currentUser {
      ... on UserNode {
        id
      }
      ... on BenchlingAppNode {
        id
      }
      ... on ServiceAccountActorNode {
        id
      }
    }
    assignedProcessTaskStatusSummaries(processTaskStatusTypes: $statusTypes) {
      ... on ProcessTaskStatusSummaryItemNode {
        count
        status {
          ... on ProcessTaskStatusNode {
            id
            displayName
            statusType
          }
        }
      }
    }
  }
`;

export const setTaskSearchFilter = (userId: string | null, statusId: string | null): void => {
  const enabledClientOrigins = getEnabledClientOrigins();

  const searchFilterTypes: Array<ModelTypeConstants> = [];
  for (const clientOrigin of enabledClientOrigins) {
    const supportedTypes = SUPPORTED_DASHBOARD_TYPES_BY_PROCESS_CLIENT_ORIGIN[clientOrigin];

    // There are multiple dashboard types (e.g. recipe unit op) that map to the PROCESS_TASK Model Type
    // When the user has multiple enabled client origins, we need to add all the possible types to the search filter
    for (const supportedType of supportedTypes) {
      if (isDashboardTypeAssignable(supportedType)) {
        searchFilterTypes.push(supportedType);
      }
    }
  }

  const uniqueSearchFilterTypes = _.unique(searchFilterTypes);

  const searchFilters: Array<Filter> = [
    {
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: naturalSort(uniqueSearchFilterTypes),
    },
  ];

  if (userId != null) {
    searchFilters.push({
      ...keyToEmptyFilter(FilterKey.PROCESS_TASK_ASSIGNEE),
      value: [userId],
    });
  }

  if (statusId != null) {
    searchFilters.push({
      ...keyToEmptyFilter(FilterKey.PROCESS_TASK_STATUS_FOR_ALL_TASK_TYPES),
      value: [statusId],
    });
  }

  // There is no "all client origins view" in the workflows dashboard. So if the user has multiple enabled,
  // we take them to global search instead.
  const navSectionToShow =
    enabledClientOrigins.length === 1 && enabledClientOrigins[0] === ProcessClientOrigin.PROCESSES
      ? NavConstants.Section.PROCESSES
      : NavConstants.Section.SEARCH;

  if (navSectionToShow === NavConstants.Section.PROCESSES) {
    searchFilters.push({
      ...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN),
      value: ProcessClientOrigin.PROCESSES,
    });
  }

  getStore().dispatch(
    SidepanelReduxActions.openListing(
      navSectionToShow,
      SearchUtil.mergeFiltersToSearchParams(getDefaultSearchSearchParams(), searchFilters)
    )
  );
  SidepanelActions.expand();

  analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.OPENED_WORKFLOW_DASHBOARD, {
    action_origin: ActionOrigin.SIDEPANEL_HOME_PROCESS_TASK_SUMMARY,
  });
};

export default function AssignedProcessTaskStatusSummary(): JSX.Element | null {
  const queryResult = useBenchlingQuery<AssignedProcessTasksQuery, AssignedProcessTasksQueryVariables>(
    ASSIGNED_PROCESS_TASKS_QUERY,
    {
      variables: {statusTypes: [ProcessTaskStatusType.PENDING, ProcessTaskStatusType.IN_PROGRESS]},
    }
  );

  let summaryRow;
  let currentUserId;

  if (queryResult.type === QueryResultType.ERROR) {
    return null;
  }
  if (queryResult.type === QueryResultType.PENDING) {
    summaryRow = (
      <nyc.Row className="homeSidepanel-today-item">
        <div className="homeSidepanel-today-sectionHeader-content">Loading assigned tasks...</div>
      </nyc.Row>
    );
  } else if (queryResult.type === QueryResultType.SUCCESS) {
    const {assignedProcessTaskStatusSummaries: processTaskStatusSummaries, currentUser} = queryResult.data;
    currentUserId = currentUser?.id;

    if (processTaskStatusSummaries.length === 0) {
      summaryRow = (
        <nyc.Row className="homeSidepanel-today-item">
          <div className="homeSidepanel-today-sectionHeader-content">You have no assigned tasks.</div>
        </nyc.Row>
      );
    } else {
      // extract the statuses from the payload's summaries, sort those statuses. Then sort the payload's summaries
      // by the relative position of those statuses
      const sortedStatusIds = sortTaskStatuses(
        processTaskStatusSummaries.map((summary) => summary.status)
      ).map((status) => status.id);
      const sortedSummaries = _.sortBy(processTaskStatusSummaries, (summary) =>
        sortedStatusIds.indexOf(summary.status.id)
      );

      const summaryItemRows: Array<JSX.Element> = [];
      for (const summary of sortedSummaries) {
        summaryItemRows.push(
          <AssignedProcessTaskStatusSummaryItem
            count={summary.count}
            displayName={summary.status.displayName}
            statusType={summary.status.statusType}
            onClick={setTaskSearchFilter.bind(null, currentUserId, summary.status.id)}
          />
        );
      }

      summaryRow = (
        <nyc.Row>
          <nyc.Col sm={6}>
            <div className="homeSidepanel-today-review">
              {summaryItemRows.map((item, index) => {
                // break summary items into rows of 2
                if (index % 2 === 0) {
                  return (
                    <div key={index} className="u-flex-display u-small-spacing-bottom">
                      {item}
                      {summaryItemRows[index + 1]}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </nyc.Col>
        </nyc.Row>
      );
    }
  }

  return (
    <nyc.Row>
      <nyc.Container size="sidepanel" className="homeSidepanel-today-itemSummary">
        <nyc.Row
          className={classNames('homeSidepanel-today-sectionHeader', {'u-pointer': currentUserId != null})}
        >
          <div
            className="homeSidepanel-today-sectionHeader-content"
            onClick={currentUserId != null ? setTaskSearchFilter.bind(null, currentUserId, null) : undefined}
          >
            Tasks assigned to me
          </div>
        </nyc.Row>
        {summaryRow}
      </nyc.Container>
    </nyc.Row>
  );
}
